import { LE } from "@/constants/enums";
import type { FormKitNode } from "@formkit/core";
import { getLookupDescription } from "@modernary/mui/components/lookup";
import {
  contactMethodItems,
  contactMethodToFormkit,
  contactMethodsForSave,
  emptyContactMethod,
  inferredKind,
  type ContactMethod,
  type ContactMethodCollection,
} from "@/views/person/person";
import { ref } from "vue";

export interface ContactKindConfig {
  /** At least one row of this kind is required. */
  required?: boolean;
  /** Lookup murns that must be present, such as `LE.ContactPhoneType.Work`. */
  requiredTypes?: string[];
  /** Lookup murns to show when the contact list is still empty. */
  initialTypes?: string[];
  /** Show one blank row of this kind when the contact list is still empty. */
  initial?: boolean;
  /** Address only. Lookup murns that show PO Box. Defaults to `LE.ContactAddressType.Mailing`. */
  poBoxTypes?: string[];
}

export type ContactMethodsConfig = Partial<Record<LE.ContactMethodType, ContactKindConfig>>;

export const personContactConfig: ContactMethodsConfig = {
  [LE.ContactMethodType.Email]: { initial: true },
  [LE.ContactMethodType.Phone]: { initial: true },
  [LE.ContactMethodType.Address]: {},
};

export const organizationContactConfig: ContactMethodsConfig = {
  [LE.ContactMethodType.Phone]: { requiredTypes: [LE.ContactPhoneType.Work] },
  [LE.ContactMethodType.Address]: {},
};

/** Form value for the contact-methods group. The API still uses one `ContactMethod[]`. */
export type ContactMethodsFormkit = Record<LE.ContactMethodType, ContactMethod[]>;

const kindOrder: LE.ContactMethodType[] = [
  LE.ContactMethodType.Email,
  LE.ContactMethodType.Phone,
  LE.ContactMethodType.Address,
];

/** Lookup descriptions for ContactMethodType, keyed by murn. */
export function useContactKindDescriptions() {
  const descriptions = ref<Partial<Record<LE.ContactMethodType, string>>>({});
  for (const kind of kindOrder) {
    void getLookupDescription(kind).then((description) => {
      descriptions.value = { ...descriptions.value, [kind]: description ?? "" };
    });
  }
  return descriptions;
}

export function visibleContactKinds(config?: ContactMethodsConfig): LE.ContactMethodType[] {
  if (!config) return [...kindOrder];
  return kindOrder.filter((kind) => config[kind] != null);
}

export function addressPoBoxTypes(config?: ContactMethodsConfig): string[] {
  return config?.[LE.ContactMethodType.Address]?.poBoxTypes ?? [LE.ContactAddressType.Mailing];
}

export function contactMethodShowsPoBox(method: ContactMethod | undefined, poBoxTypes: string[]): boolean {
  if (!method || inferredKind(method) !== LE.ContactMethodType.Address) return false;
  return poBoxTypes.some((typeMurn) => method.typeMurn === typeMurn);
}

export function stripDisallowedPoBox(method: ContactMethod, poBoxTypes: string[]): void {
  if (contactMethodShowsPoBox(method, poBoxTypes)) return;
  if (inferredKind(method) !== LE.ContactMethodType.Address) return;
  if (method.poBox) method.poBox = "";
  if (method.murnAddress?.poBox) method.murnAddress.poBox = "";
}

export function contactTypeIsRequired(
  config: ContactMethodsConfig | undefined,
  kind: LE.ContactMethodType,
  method: ContactMethod | undefined,
): boolean {
  if (!method) return false;
  const typeMurns = config?.[kind]?.requiredTypes ?? [];
  return typeMurns.some((typeMurn) => method.typeMurn === typeMurn);
}

export function isContactMethodsFormkit(value: unknown): value is ContactMethodsFormkit {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return Object.values(LE.ContactMethodType).some((kind) => Array.isArray(record[kind]));
}

/** Split an API contact-method list into the three repeater arrays. */
export function contactMethodsToFormkit(
  methods: ContactMethodCollection | ContactMethodsFormkit | null | undefined,
  config?: ContactMethodsConfig,
): ContactMethodsFormkit {
  const visible = visibleContactKinds(config);
  const source = isContactMethodsFormkit(methods) ? flattenContactMethods(methods) : contactMethodItems(methods);
  const lists = emptyContactMethodsFormkit();
  const seedInitial = source.length === 0;

  for (const method of source) {
    const normalized = contactMethodToFormkit({
      ...method,
      murnAddress: method.murnAddress ? { ...method.murnAddress } : undefined,
    });
    const kind = inferredKind(normalized);
    if (!visible.includes(kind)) continue;
    lists[kind].push(tagKind(normalized, kind));
  }

  for (const kind of visible) {
    const kindConfig = config?.[kind];
    if (seedInitial && kindConfig?.initial && lists[kind].length === 0) {
      lists[kind].push(emptyContactMethod(kind));
    }
    if (seedInitial) {
      for (const typeMurn of kindConfig?.initialTypes ?? []) {
        if (!lists[kind].some((method) => method.typeMurn === typeMurn)) {
          lists[kind].push(contactMethodWithType(kind, typeMurn));
        }
      }
    }
    for (const typeMurn of kindConfig?.requiredTypes ?? []) {
      if (!lists[kind].some((method) => method.typeMurn === typeMurn)) {
        lists[kind].push(contactMethodWithType(kind, typeMurn));
      }
    }
  }

  return lists;
}

/** Collapse the three repeater arrays into the API contact-method list. */
export function contactMethodsFromFormkit(
  value: ContactMethodsFormkit | ContactMethodCollection | null | undefined,
  config?: ContactMethodsConfig,
): ContactMethod[] {
  const methods = isContactMethodsFormkit(value) ? flattenContactMethods(value) : contactMethodItems(value);
  const poBoxTypes = addressPoBoxTypes(config);
  for (const method of methods) stripDisallowedPoBox(method, poBoxTypes);
  return contactMethodsForSave(methods);
}

/** Fails when a repeater is missing one of the lookup murns passed as validation args. */
export async function required_contact_types(node: FormKitNode, ...typeMurns: string[]): Promise<boolean> {
  const methods = Array.isArray(node.value) ? (node.value as ContactMethod[]) : [];
  const missing = typeMurns.find((typeMurn) => !methods.some((method) => method.typeMurn === typeMurn));
  const description = missing ? await getLookupDescription(missing) : "";
  node.props.requiredContactTypeMessage = missing ? `${description || "This contact type"} is required.` : "";
  return !missing;
}

/** Counts rows across the contact-methods group. */
export function min_contact_methods(node: FormKitNode, minimum?: string | number): boolean {
  const min = Number(minimum ?? 1);
  const value = node.value as Partial<ContactMethodsFormkit> | undefined;
  const count = kindOrder.reduce((sum, kind) => sum + (value?.[kind]?.length ?? 0), 0);
  node.props.minContactMethodsMessage =
    min === 1 ? "At least one contact method is required." : `At least ${min} contact methods are required.`;
  return !Number.isFinite(min) || min <= 0 || count >= min;
}

function emptyContactMethodsFormkit(): ContactMethodsFormkit {
  return {
    [LE.ContactMethodType.Email]: [],
    [LE.ContactMethodType.Phone]: [],
    [LE.ContactMethodType.Address]: [],
  };
}

function flattenContactMethods(value: ContactMethodsFormkit): ContactMethod[] {
  return kindOrder.flatMap((kind) => (value[kind] ?? []).map((method) => tagKind(method, kind)));
}

function contactMethodWithType(kind: LE.ContactMethodType, typeMurn: string): ContactMethod {
  const method = emptyContactMethod(kind);
  method.typeMurn = typeMurn;
  return method;
}

function tagKind(method: ContactMethod, kind: LE.ContactMethodType): ContactMethod {
  if (method.kind !== kind) method.kind = kind;
  return method;
}
